import { useCallback, useEffect, useRef, useState } from 'react'

export type AiRunState = 'idle' | 'running' | 'complete' | 'error'

interface UseAiRunOptions {
  stages: string[]
  stageDuration?: number
  failureRate?: number
}

interface UseAiRunResult {
  state: AiRunState
  stageIndex: number
  currentStage: string | null
  progress: number
  start: () => void
  reset: () => void
}

/**
 * Simulates a staged AI run (scoring, analysis, report generation) so the
 * intelligence layer reads as real work rather than an instant result.
 */
export function useAiRun({
  stages,
  stageDuration = 900,
  failureRate = 0
}: UseAiRunOptions): UseAiRunResult {
  const [state, setState] = useState<AiRunState>('idle')
  const [stageIndex, setStageIndex] = useState(-1)
  const timers = useRef<number[]>([])

  const clear = useCallback(() => {
    timers.current.forEach((t) => window.clearTimeout(t))
    timers.current = []
  }, [])

  useEffect(() => clear, [clear])

  const start = useCallback(() => {
    clear()
    setState('running')
    setStageIndex(0)
    stages.forEach((_, index) => {
      const timer = window.setTimeout(() => {
        if (index === stages.length - 1) {
          const failed = Math.random() < failureRate
          setState(failed ? 'error' : 'complete')
          setStageIndex(stages.length - 1)
        } else {
          setStageIndex(index + 1)
        }
      }, stageDuration * (index + 1))
      timers.current.push(timer)
    })
  }, [clear, failureRate, stageDuration, stages])

  const reset = useCallback(() => {
    clear()
    setState('idle')
    setStageIndex(-1)
  }, [clear])

  const progress =
    state === 'complete'
      ? 100
      : state === 'idle'
        ? 0
        : Math.round(((stageIndex + 1) / stages.length) * 100)

  return {
    state,
    stageIndex,
    currentStage: stageIndex >= 0 ? stages[Math.min(stageIndex, stages.length - 1)] : null,
    progress,
    start,
    reset
  }
}
